import React from "react";
import { Box, UnstyledButton } from "@mantine/core";
import { IconCircleCheck, IconCircle } from "@tabler/icons-react";
import styles from "./CheckoutPanel.module.css";

interface RadioRingRowProps {
  selected: boolean;
  onSelect: () => void;
  /** Row heading content (name + optional chips). */
  title: React.ReactNode;
  /** Dimmed caption under the title. */
  caption?: React.ReactNode;
  /** Content between the radio icon and the body (e.g. card brand badge). */
  leading?: React.ReactNode;
  /** Right-aligned content (e.g. edit button). */
  rightSection?: React.ReactNode;
  /** Extra content under title/caption (e.g. remaining chips). */
  children?: React.ReactNode;
  disabled?: boolean;
}

/**
 * Selectable radio row per the design: check-circle indicator, teal inset
 * ring + tinted fill when selected. Used for terminals, saved cards, and
 * packages in the checkout method reveals.
 */
export default function RadioRingRow({
  selected,
  onSelect,
  title,
  caption,
  leading,
  rightSection,
  children,
  disabled = false,
}: RadioRingRowProps) {
  const classNames = [styles.ringRow];
  if (selected) classNames.push(styles.ringRowSelected);
  if (disabled) classNames.push(styles.ringRowDisabled);

  return (
    <UnstyledButton
      className={classNames.join(" ")}
      onClick={() => {
        if (!disabled) onSelect();
      }}
      role="radio"
      aria-checked={selected}
      aria-disabled={disabled}
    >
      <Box className={styles.ringRowRadio}>
        {selected ? (
          <IconCircleCheck size={20} color="var(--brand-teal)" />
        ) : (
          <IconCircle size={20} color="#c7d2d4" />
        )}
      </Box>
      {leading}
      <Box className={styles.ringRowBody}>
        <Box className={styles.ringRowTitle}>{title}</Box>
        {caption && <Box className={styles.ringRowCaption}>{caption}</Box>}
        {children}
      </Box>
      {rightSection}
    </UnstyledButton>
  );
}
